(() => {
  if (customElements.get('neisstech-animated-number')) return;

  class NeisstechCountingNumber extends HTMLElement {
    connectedCallback() {
      if (this.dataset.countingReady === 'true') return;
      this.dataset.countingReady = 'true';

      this.valueElement = this.querySelector('[data-counting-number-value]');
      this.finalText = this.dataset.countingValue || this.textContent.trim();
      this.duration = Math.min(Math.max(Number(this.dataset.countingDuration) || 1600, 200), 5000);

      if (
        !this.valueElement ||
        this.dataset.countingEnabled !== 'true' ||
        window.matchMedia('(prefers-reduced-motion: reduce)').matches
      ) {
        this.showFinalValue();
        return;
      }

      const numberMatch = this.finalText.match(/-?\d+(?:[.,]\d+)?/);
      if (!numberMatch) {
        this.showFinalValue();
        return;
      }

      this.numberPrefix = this.finalText.slice(0, numberMatch.index);
      this.numberSuffix = this.finalText.slice(numberMatch.index + numberMatch[0].length);
      this.target = Number(numberMatch[0].replace(',', '.'));
      this.decimals = (numberMatch[0].split(/[.,]/)[1] || '').length;

      if (!Number.isFinite(this.target)) {
        this.showFinalValue();
        return;
      }

      if (!('IntersectionObserver' in window)) {
        this.frame = requestAnimationFrame(() => this.startAnimation());
        return;
      }

      this.observer = new IntersectionObserver(
        (entries) => {
          if (!entries.some((entry) => entry.isIntersecting)) return;
          this.observer.disconnect();
          this.startAnimation();
        },
        { threshold: 0.25, rootMargin: '0px 0px -10% 0px' },
      );

      this.observer.observe(this);
    }

    disconnectedCallback() {
      this.observer?.disconnect();
      if (this.frame) cancelAnimationFrame(this.frame);
    }

    startAnimation() {
      this.dataset.countingAnimated = 'true';
      const finalWidth = this.valueElement.getBoundingClientRect().width;
      if (finalWidth > 0) this.style.minInlineSize = `${finalWidth}px`;

      const startedAt = performance.now();
      const renderFrame = (now) => {
        const progress = Math.min((now - startedAt) / this.duration, 1);
        const eased = progress < 0.5
          ? 2 * progress * progress
          : 1 - Math.pow(-2 * progress + 2, 2) / 2;
        const currentValue = this.target * eased;
        const visibleNumber = new Intl.NumberFormat(document.documentElement.lang || 'es-CL', {
          minimumFractionDigits: this.decimals,
          maximumFractionDigits: this.decimals,
        }).format(currentValue);

        this.valueElement.textContent = `${this.numberPrefix}${visibleNumber}${this.numberSuffix}`;

        if (progress < 1) {
          this.frame = requestAnimationFrame(renderFrame);
        } else {
          this.showFinalValue();
        }
      };

      this.frame = requestAnimationFrame(renderFrame);
    }

    showFinalValue() {
      if (this.valueElement) this.valueElement.textContent = this.finalText;
      this.style.removeProperty('min-inline-size');
    }
  }

  customElements.define('neisstech-animated-number', NeisstechCountingNumber);
})();
